import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Shield, ChevronRight, Check, Loader2, FileText, Award,
  Clock, CheckCircle2, AlertTriangle, BookOpen, Target,
  Sparkles, Lock, ArrowRight, RefreshCw, ChevronDown,
  Users, BarChart3, ClipboardCheck, Activity, Building, Search,
  Zap, Eye, Trophy, Medal, Send, TrendingUp, Flame
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { useAuth } from '../context/AuthContext';

const PHASE_CONFIG = {
  PLAN: { color: 'bg-blue-600', light: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', ring: 'ring-blue-400' },
  DO: { color: 'bg-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', ring: 'ring-emerald-400' },
  CHECK: { color: 'bg-amber-600', light: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', ring: 'ring-amber-400' },
  ACT: { color: 'bg-violet-600', light: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', ring: 'ring-violet-400' },
};

const POLICY_ICONS = {
  bcm_policy: Shield, bia_policy: Search, risk_assessment_policy: AlertTriangle,
  threat_profiling_policy: Target, asset_management_policy: Building,
  stakeholder_policy: Users, recovery_strategy_policy: FileText,
  crisis_management_policy: AlertTriangle, incident_response_policy: Clock,
  communication_policy: FileText, training_policy: BookOpen,
  exercise_testing_policy: Activity, internal_audit_policy: ClipboardCheck,
  performance_evaluation_policy: BarChart3, continual_improvement_policy: RefreshCw,
  management_review_policy: Users,
};

const LIFECYCLE_STEPS = [
  { id: 'published', label: 'Policies Published', desc: 'Organization policy library' },
  { id: 'assigned', label: 'Role-Based Assignment', desc: 'Filtered to your role' },
  { id: 'study', label: 'Study & Review', desc: 'Read policies & AI briefings' },
  { id: 'acknowledge', label: 'Acknowledge All', desc: 'Digitally sign each policy' },
  { id: 'certified', label: 'Annual Certification', desc: 'Compliance verified' },
  { id: 'renewal', label: 'Renewal (365d)', desc: 'Auto-tracked expiry' },
];

export default function PolicyComplianceDashboard() {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [curriculum, setCurriculum] = useState([]);
  const [progress, setProgress] = useState(null);
  const [certification, setCertification] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [showBriefingDialog, setShowBriefingDialog] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [certifying, setCertifying] = useState(false);
  const [showCertReport, setShowCertReport] = useState(false);
  const [certReport, setCertReport] = useState(null);
  const [activePhaseFilter, setActivePhaseFilter] = useState('ALL');
  const [leaderboard, setLeaderboard] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [nudging, setNudging] = useState(null);

  const isAdmin = user?.role === 'bcm_manager' || user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const requests = [
        api.get('/training/policy-curriculum'),
        api.get('/training/policy-curriculum/my-certification'),
      ];
      const [currRes, certRes] = await Promise.all(requests);
      setCurriculum(currRes.data.curriculum || []);
      setProgress(currRes.data.progress || {});
      setCertification(certRes.data);
    } catch (err) {
      toast.error('Failed to load policy compliance data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAcknowledge = async (policyType) => {
    setAcknowledging(true);
    try {
      await api.post(`/training/policy-curriculum/${policyType}/acknowledge`);
      toast.success('Policy acknowledged successfully');
      await fetchData();
      setShowPolicyDialog(false);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to acknowledge policy';
      toast.error(typeof detail === 'string' ? detail : detail.message || 'Error');
    } finally {
      setAcknowledging(false);
    }
  };

  const handleGenerateBriefing = async (policyType) => {
    setBriefingLoading(true);
    setBriefing(null);
    setShowBriefingDialog(true);
    try {
      const res = await api.post(`/training/policy-curriculum/ai-briefing/${policyType}`);
      setBriefing(res.data);
    } catch (err) {
      toast.error('Failed to generate AI briefing');
      setShowBriefingDialog(false);
    } finally {
      setBriefingLoading(false);
    }
  };

  const handleIssueCertification = async () => {
    setCertifying(true);
    try {
      const res = await api.post('/training/policy-curriculum/annual-certification');
      toast.success(res.data.message);
      await fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        toast.error(detail.message || 'Cannot issue certification');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to issue certification');
      }
    } finally {
      setCertifying(false);
    }
  };

  const handleFetchCertReport = async () => {
    try {
      const res = await api.get('/training/policy-curriculum/certification-report');
      setCertReport(res.data);
      setShowCertReport(true);
    } catch (err) {
      toast.error('Failed to load certification report');
    }
  };

  const handleFetchLeaderboard = async () => {
    try {
      const res = await api.get('/training/policy-curriculum/leaderboard');
      setLeaderboard(res.data);
      setShowLeaderboard(true);
    } catch (err) {
      toast.error('Failed to load leaderboard');
    }
  };

  const handleNudge = async (userId) => {
    setNudging(userId);
    try {
      const res = await api.post(`/training/policy-curriculum/nudge/${userId}`);
      toast.success(res.data.message);
      // Refresh leaderboard
      const lbRes = await api.get('/training/policy-curriculum/leaderboard');
      setLeaderboard(lbRes.data);
    } catch (err) {
      toast.error('Failed to send nudge');
    } finally {
      setNudging(null);
    }
  };

  // Compute lifecycle step
  const getLifecycleStep = () => {
    if (!progress) return 0;
    const relevant = curriculum.filter(c => c.is_relevant);
    const published = relevant.filter(c => c.policy_status !== 'not_created').length;
    const completed = progress.completed || 0;
    const total = progress.total_relevant || 0;

    if (certification?.has_certification) return 4; // certified
    if (completed === total && total > 0) return 3; // all acknowledged, ready to certify
    if (completed > 0) return 2; // some acknowledged, studying
    if (published > 0) return 1; // policies exist, assigned
    return 0; // initial
  };

  const currentStep = getLifecycleStep();

  // Filter policies
  const relevantPolicies = curriculum.filter(c => c.is_relevant);
  const filteredPolicies = activePhaseFilter === 'ALL'
    ? relevantPolicies
    : relevantPolicies.filter(c => c.phase === activePhaseFilter);

  const phaseCounts = {};
  relevantPolicies.forEach(p => {
    if (!phaseCounts[p.phase]) phaseCounts[p.phase] = { total: 0, done: 0 };
    phaseCounts[p.phase].total++;
    if (p.ack_status === 'completed') phaseCounts[p.phase].done++;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]" data-testid="policy-compliance-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="policy-compliance-dashboard">
      {/* ─── Lifecycle Map ─── */}
      <Card className="overflow-hidden border-slate-200" data-testid="lifecycle-map">
        <CardHeader className="pb-3 bg-gradient-to-r from-slate-900 to-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white text-lg">Policy Compliance Lifecycle</CardTitle>
              <CardDescription className="text-slate-300">
                Your annual certification journey — {progress?.total_relevant || 0} policies assigned to your role
              </CardDescription>
            </div>
            {certification?.has_certification && (
              <Badge className="bg-emerald-500 text-white border-0 text-sm px-3 py-1" data-testid="cert-active-badge">
                <Award className="w-4 h-4 mr-1.5" />
                Certified — {certification.days_until_expiry}d remaining
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-6 pb-4 px-4 sm:px-6">
          <div className="flex items-center justify-between overflow-x-auto pb-2" data-testid="lifecycle-steps">
            {LIFECYCLE_STEPS.map((step, idx) => {
              const isComplete = idx < currentStep;
              const isActive = idx === currentStep;
              const isPending = idx > currentStep;
              return (
                <React.Fragment key={step.id}>
                  {idx > 0 && (
                    <div className={`flex-1 h-0.5 mx-1 min-w-[16px] transition-colors duration-500 ${
                      isComplete ? 'bg-emerald-500' : isActive ? 'bg-blue-400' : 'bg-slate-200'
                    }`} />
                  )}
                  <div className={`flex flex-col items-center min-w-[90px] transition-all duration-300 ${isActive ? 'scale-105' : ''}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-500 ${
                      isComplete
                        ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200'
                        : isActive
                          ? 'bg-blue-600 text-white ring-4 ring-blue-200 shadow-md'
                          : 'bg-slate-100 text-slate-400 border border-slate-200'
                    }`}>
                      {isComplete ? <Check className="w-4 h-4" /> : idx + 1}
                    </div>
                    <p className={`text-xs mt-2 font-medium text-center leading-tight ${
                      isActive ? 'text-blue-700' : isComplete ? 'text-emerald-700' : 'text-slate-400'
                    }`}>
                      {step.label}
                    </p>
                    <p className="text-[10px] text-slate-400 text-center mt-0.5 hidden sm:block">{step.desc}</p>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ─── Progress & Certification Row ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Progress Card */}
        <Card className="lg:col-span-2" data-testid="compliance-progress">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-slate-900">Compliance Progress</h3>
                <p className="text-sm text-slate-500">
                  {progress?.completed || 0} of {progress?.total_relevant || 0} policies acknowledged
                </p>
              </div>
              <span className={`text-2xl font-bold ${
                (progress?.percentage || 0) === 100 ? 'text-emerald-600' : 'text-slate-800'
              }`}>
                {progress?.percentage || 0}%
              </span>
            </div>
            <Progress
              value={progress?.percentage || 0}
              className="h-3"
              data-testid="compliance-progress-bar"
            />
            <div className="flex gap-3 mt-4 flex-wrap">
              {Object.entries(phaseCounts).map(([phase, counts]) => {
                const cfg = PHASE_CONFIG[phase] || PHASE_CONFIG.PLAN;
                return (
                  <div
                    key={phase}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium ${cfg.light} ${cfg.text} ${cfg.border} border`}
                  >
                    <span>{phase}</span>
                    <span className="font-bold">{counts.done}/{counts.total}</span>
                  </div>
                );
              })}
            </div>
            {progress?.earliest_expiry_days !== null && progress?.earliest_expiry_days !== undefined && (
              <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Earliest renewal in {progress.earliest_expiry_days} days
              </p>
            )}
          </CardContent>
        </Card>

        {/* Certification Card */}
        <Card className={`${certification?.has_certification ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200'}`} data-testid="certification-card">
          <CardContent className="pt-6 flex flex-col items-center text-center h-full justify-center">
            {certification?.has_certification ? (
              <>
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                  <Award className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-emerald-800">Certified</h3>
                <p className="text-sm text-emerald-600 mt-1">
                  Annual Policy Compliance
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Expires in {certification.days_until_expiry} days
                </p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                  {(progress?.percentage || 0) === 100
                    ? <Zap className="w-7 h-7 text-amber-500" />
                    : <Lock className="w-7 h-7 text-slate-400" />
                  }
                </div>
                <h3 className="font-semibold text-slate-700">
                  {(progress?.percentage || 0) === 100 ? 'Ready to Certify' : 'Certification Locked'}
                </h3>
                <p className="text-xs text-slate-500 mt-1 mb-3">
                  {(progress?.percentage || 0) === 100
                    ? 'All policies acknowledged — issue your annual certification'
                    : `Acknowledge all ${progress?.total_relevant || 0} policies to unlock`
                  }
                </p>
                <Button
                  size="sm"
                  disabled={(progress?.percentage || 0) < 100 || certifying}
                  onClick={handleIssueCertification}
                  className={`${(progress?.percentage || 0) === 100 ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                  data-testid="issue-certification-btn"
                >
                  {certifying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Award className="w-4 h-4 mr-1" />}
                  Issue Certification
                </Button>
              </>
            )}
            {isAdmin && (
              <div className="flex gap-2 mt-2">
                <Button variant="link" size="sm" className="text-xs" onClick={handleFetchCertReport} data-testid="cert-report-btn">
                  Org Report
                </Button>
                <Button variant="link" size="sm" className="text-xs text-amber-600" onClick={handleFetchLeaderboard} data-testid="leaderboard-btn">
                  <Trophy className="w-3 h-3 mr-1" /> Leaderboard
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── Phase Filter Tabs ─── */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="phase-filter">
        <Button
          variant={activePhaseFilter === 'ALL' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActivePhaseFilter('ALL')}
          className="text-xs"
        >
          All ({relevantPolicies.length})
        </Button>
        {['PLAN', 'DO', 'CHECK', 'ACT'].map(phase => {
          const cfg = PHASE_CONFIG[phase];
          const count = phaseCounts[phase]?.total || 0;
          if (count === 0) return null;
          return (
            <Button
              key={phase}
              variant={activePhaseFilter === phase ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActivePhaseFilter(phase)}
              className={`text-xs ${activePhaseFilter === phase ? cfg.color + ' text-white hover:opacity-90' : ''}`}
            >
              {phase} ({count})
            </Button>
          );
        })}
      </div>

      {/* ─── Policy Cards Grid ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="policy-cards-grid">
        {filteredPolicies.map(policy => {
          const cfg = PHASE_CONFIG[policy.phase] || PHASE_CONFIG.PLAN;
          const IconComp = POLICY_ICONS[policy.policy_type] || FileText;
          const isAcked = policy.ack_status === 'completed';
          const isExpired = policy.ack_status === 'expired';

          return (
            <Card
              key={policy.policy_type}
              className={`relative overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer group ${
                isAcked ? 'border-emerald-200 bg-emerald-50/20' : isExpired ? 'border-red-200 bg-red-50/20' : 'border-slate-200'
              }`}
              onClick={() => { setSelectedPolicy(policy); setShowPolicyDialog(true); }}
              data-testid={`policy-card-${policy.policy_type}`}
            >
              {/* Phase stripe */}
              <div className={`absolute top-0 left-0 w-1 h-full ${cfg.color}`} />
              <CardContent className="pt-5 pl-5">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg ${cfg.light} flex items-center justify-center shrink-0`}>
                    <IconComp className={`w-5 h-5 ${cfg.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className={`text-[10px] ${cfg.text} ${cfg.border}`}>
                        {policy.phase}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] text-slate-500">
                        ISO {policy.iso_clause}
                      </Badge>
                      {isAcked && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">
                          <Check className="w-2.5 h-2.5 mr-0.5" /> Acknowledged
                        </Badge>
                      )}
                      {isExpired && (
                        <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">
                          Expired
                        </Badge>
                      )}
                    </div>
                    <h4 className="font-semibold text-sm text-slate-900 leading-snug truncate">
                      {policy.short_title || policy.title}
                    </h4>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {policy.summary_text || policy.design_rationale}
                    </p>
                    {isAcked && policy.days_remaining && (
                      <p className="text-[10px] text-emerald-600 mt-1.5 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> Renewal in {policy.days_remaining}d
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 mt-3">
                      <Button variant="ghost" size="sm" className="h-7 text-xs px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        onClick={(e) => { e.stopPropagation(); setSelectedPolicy(policy); setShowPolicyDialog(true); }}
                      >
                        <Eye className="w-3 h-3 mr-1" /> View
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs px-2 text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                        onClick={(e) => { e.stopPropagation(); handleGenerateBriefing(policy.policy_type); }}
                      >
                        <Sparkles className="w-3 h-3 mr-1" /> AI Brief
                      </Button>
                      {!isAcked && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs px-2 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 ml-auto"
                          onClick={(e) => { e.stopPropagation(); handleAcknowledge(policy.policy_type); }}
                          data-testid={`ack-btn-${policy.policy_type}`}
                        >
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Acknowledge
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ─── Policy Detail Dialog ─── */}
      <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedPolicy && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`${PHASE_CONFIG[selectedPolicy.phase]?.color} text-white`}>
                    {selectedPolicy.phase}
                  </Badge>
                  <Badge variant="outline">ISO {selectedPolicy.iso_clause}</Badge>
                  {selectedPolicy.ack_status === 'completed' && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0">
                      <Check className="w-3 h-3 mr-1" /> Acknowledged
                    </Badge>
                  )}
                </div>
                <DialogTitle>{selectedPolicy.title || selectedPolicy.short_title}</DialogTitle>
                <DialogDescription>{selectedPolicy.summary_text}</DialogDescription>
              </DialogHeader>

              <div className="space-y-5 py-4">
                {/* Data Flow */}
                {selectedPolicy.data_flow && Object.keys(selectedPolicy.data_flow).length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-4" data-testid="policy-data-flow">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <ArrowRight className="w-4 h-4" /> System Data Flow
                    </h4>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div>
                        <p className="font-medium text-blue-700 mb-1">Inputs</p>
                        <ul className="space-y-0.5 text-slate-600">
                          {(selectedPolicy.data_flow.inputs || []).map((i, idx) => (
                            <li key={idx} className="flex items-start gap-1">
                              <ChevronRight className="w-3 h-3 mt-0.5 text-blue-400 shrink-0" /> {i}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="font-medium text-emerald-700 mb-1">Outputs</p>
                        <ul className="space-y-0.5 text-slate-600">
                          {(selectedPolicy.data_flow.outputs || []).map((o, idx) => (
                            <li key={idx} className="flex items-start gap-1">
                              <ChevronRight className="w-3 h-3 mt-0.5 text-emerald-400 shrink-0" /> {o}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="font-medium text-violet-700 mb-1">Feeds Into</p>
                        <ul className="space-y-0.5 text-slate-600">
                          {(selectedPolicy.data_flow.feeds_into || []).map((f, idx) => (
                            <li key={idx} className="flex items-start gap-1">
                              <ChevronRight className="w-3 h-3 mt-0.5 text-violet-400 shrink-0" /> {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Design Rationale */}
                {selectedPolicy.design_rationale && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Architecture Rationale</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{selectedPolicy.design_rationale}</p>
                  </div>
                )}

                {/* Process Owner Role */}
                {selectedPolicy.process_owner_role && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <h4 className="text-sm font-semibold text-blue-800 mb-1">Your Role (Process Owner)</h4>
                    <p className="text-sm text-blue-700 leading-relaxed">{selectedPolicy.process_owner_role}</p>
                  </div>
                )}

                {/* Policy Content */}
                {selectedPolicy.policy_text && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Policy Content</h4>
                    <div className="text-sm text-slate-600 leading-relaxed bg-white border rounded-lg p-4 max-h-60 overflow-y-auto whitespace-pre-wrap">
                      {selectedPolicy.policy_text}
                    </div>
                  </div>
                )}

                {/* Functional Narrative */}
                {selectedPolicy.functional_narrative && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Module Functionality</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{selectedPolicy.functional_narrative}</p>
                  </div>
                )}
              </div>

              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setShowPolicyDialog(false)}>Close</Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowPolicyDialog(false); handleGenerateBriefing(selectedPolicy.policy_type); }}
                >
                  <Sparkles className="w-4 h-4 mr-1.5" /> AI Briefing
                </Button>
                {selectedPolicy.ack_status !== 'completed' && (
                  <Button
                    onClick={() => handleAcknowledge(selectedPolicy.policy_type)}
                    disabled={acknowledging}
                    data-testid="dialog-acknowledge-btn"
                  >
                    {acknowledging ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                    Acknowledge Policy
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── AI Briefing Dialog ─── */}
      <Dialog open={showBriefingDialog} onOpenChange={setShowBriefingDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              AI Training Briefing
            </DialogTitle>
            {briefing && (
              <DialogDescription>
                {briefing.title} — personalized for {briefing.user_role}
              </DialogDescription>
            )}
          </DialogHeader>

          {briefingLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
              <p className="text-sm text-slate-500">Generating personalized briefing with AI...</p>
            </div>
          ) : briefing?.briefing ? (
            <div className="space-y-5 py-4">
              {/* Priority */}
              <div className="flex items-center gap-2">
                <Badge className={`${
                  briefing.briefing.priority_level === 'high' ? 'bg-red-100 text-red-700' :
                  briefing.briefing.priority_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {briefing.briefing.priority_level?.toUpperCase()} priority for your role
                </Badge>
                {!briefing.ai_generated && (
                  <Badge variant="outline" className="text-xs text-slate-500">Template-based</Badge>
                )}
              </div>

              {/* Executive Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-1">Executive Summary</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{briefing.briefing.executive_summary}</p>
              </div>

              {/* Key Takeaways */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Key Takeaways</h4>
                <ul className="space-y-1.5">
                  {(briefing.briefing.key_takeaways || []).map((t, idx) => (
                    <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Your Responsibilities */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">Your Responsibilities</h4>
                <ul className="space-y-1.5">
                  {(briefing.briefing.your_responsibilities || []).map((r, idx) => (
                    <li key={idx} className="text-sm text-blue-700 flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" /> {r}
                    </li>
                  ))}
                </ul>
              </div>

              {/* System Interaction */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">ResiliencyHUB System Interaction</h4>
                <ul className="space-y-1.5">
                  {(briefing.briefing.system_interaction_points || []).map((s, idx) => (
                    <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                      <Zap className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quiz Prep */}
              <div className="bg-violet-50 rounded-lg p-4 border border-violet-100">
                <h4 className="text-sm font-semibold text-violet-800 mb-2">Compliance Check Hints</h4>
                <ul className="space-y-1.5">
                  {(briefing.briefing.quiz_prep_hints || []).map((h, idx) => (
                    <li key={idx} className="text-sm text-violet-700 flex items-start gap-2">
                      <Target className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" /> {h}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBriefingDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Certification Report Dialog (Admin) ─── */}
      <Dialog open={showCertReport} onOpenChange={setShowCertReport}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Organization Certification Report</DialogTitle>
            <DialogDescription>
              {certReport?.summary?.certified_users || 0} of {certReport?.summary?.total_users || 0} users certified
              ({certReport?.summary?.certification_rate || 0}%)
            </DialogDescription>
          </DialogHeader>

          {certReport && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Certified</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Policies</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {certReport.report?.map((r, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{r.name}</p>
                        <p className="text-xs text-slate-500">{r.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{r.role?.replace('_', ' ')}</TableCell>
                    <TableCell>
                      <Badge className={r.is_certified ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                        {r.is_certified ? 'Certified' : 'Not Certified'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.certified_at ? new Date(r.certified_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-sm">{r.policies_covered || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCertReport(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Leaderboard Dialog ─── */}
      <Dialog open={showLeaderboard} onOpenChange={setShowLeaderboard}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Compliance Leaderboard
            </DialogTitle>
            <DialogDescription>
              Org average: {leaderboard?.org_avg_completion || 0}% — {leaderboard?.total_users || 0} team members
            </DialogDescription>
          </DialogHeader>

          {leaderboard && (
            <div className="space-y-6">
              {/* Role Group Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {leaderboard.role_summary?.map((rs, idx) => (
                  <div
                    key={idx}
                    className={`rounded-lg p-3 text-center border ${
                      rs.avg_completion >= 80 ? 'bg-emerald-50 border-emerald-200' :
                      rs.avg_completion >= 40 ? 'bg-amber-50 border-amber-200' :
                      'bg-red-50 border-red-200'
                    }`}
                  >
                    <p className="text-xs font-medium text-slate-600 truncate">{rs.role}</p>
                    <p className={`text-xl font-bold mt-1 ${
                      rs.avg_completion >= 80 ? 'text-emerald-700' :
                      rs.avg_completion >= 40 ? 'text-amber-700' :
                      'text-red-700'
                    }`}>
                      {rs.avg_completion}%
                    </p>
                    <p className="text-[10px] text-slate-500">
                      {rs.total_users} user{rs.total_users !== 1 ? 's' : ''} · {rs.certified} certified
                    </p>
                  </div>
                ))}
              </div>

              {/* Nudge Targets Banner */}
              {leaderboard.nudge_targets?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-800 font-medium">
                      {leaderboard.nudge_targets.length} team member{leaderboard.nudge_targets.length !== 1 ? 's' : ''} below 50% — consider sending a nudge
                    </span>
                  </div>
                </div>
              )}

              {/* Rankings Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="w-48">Progress</TableHead>
                    <TableHead className="w-20">Streak</TableHead>
                    <TableHead className="w-24">Status</TableHead>
                    {isAdmin && <TableHead className="w-24">Action</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboard.leaderboard?.map((entry) => {
                    const isNudgeTarget = leaderboard.nudge_targets?.some(n => n.user_id === entry.user_id);
                    return (
                      <TableRow key={entry.user_id} className={isNudgeTarget ? 'bg-amber-50/50' : ''}>
                        <TableCell>
                          <div className="flex items-center justify-center">
                            {entry.rank === 1 ? (
                              <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center">
                                <Trophy className="w-4 h-4 text-amber-600" />
                              </div>
                            ) : entry.rank === 2 ? (
                              <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center">
                                <Medal className="w-4 h-4 text-slate-500" />
                              </div>
                            ) : entry.rank === 3 ? (
                              <div className="w-7 h-7 rounded-full bg-sky-100 flex items-center justify-center">
                                <Medal className="w-4 h-4 text-sky-700" />
                              </div>
                            ) : (
                              <span className="text-sm text-slate-500 font-medium">{entry.rank}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{entry.name}</p>
                            <p className="text-xs text-slate-500">{entry.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{entry.role_label}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <Progress value={entry.percentage} className="h-2" />
                            </div>
                            <span className={`text-xs font-bold min-w-[36px] text-right ${
                              entry.percentage === 100 ? 'text-emerald-600' :
                              entry.percentage >= 50 ? 'text-blue-600' :
                              'text-slate-500'
                            }`}>
                              {entry.percentage}%
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">{entry.completed}/{entry.total_required}</p>
                        </TableCell>
                        <TableCell>
                          {entry.streak > 0 ? (
                            <div className="flex items-center gap-1 text-amber-600">
                              <Flame className="w-3.5 h-3.5" />
                              <span className="text-xs font-bold">{entry.streak}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.is_certified ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">
                              <Award className="w-3 h-3 mr-0.5" /> Certified
                            </Badge>
                          ) : entry.percentage === 100 ? (
                            <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">Ready</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 border-0 text-[10px]">In Progress</Badge>
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            {isNudgeTarget && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                                onClick={() => handleNudge(entry.user_id)}
                                disabled={nudging === entry.user_id}
                                data-testid={`nudge-btn-${entry.user_id}`}
                              >
                                {nudging === entry.user_id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <><Send className="w-3 h-3 mr-1" /> Nudge</>
                                )}
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaderboard(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
